import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { Box, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { getCommunityDetailsByIdQuery, useApi } from "@/api";
import LoadingPane from "@/components/LoadingPane";
import { canViewCommunitySettings } from "@/security/logic";
import { RoutedTabs } from "@/components/RoutedTabs";

export const Route = createFileRoute("/settings/community/$communityId")({
  component: RouteComponent,
  beforeLoad: async ({ context, params }) => {
    const communityId = Number(params.communityId);
    const { secureAxios } = context.apiHook;
    await context.queryClient.ensureQueryData(
      getCommunityDetailsByIdQuery(secureAxios, communityId)
    );
  },
  loader: ({ context, params, location }) => {
    const { userClaim } = context;
    if (!canViewCommunitySettings(userClaim, Number(params.communityId))) {
      throw redirect({
        to: "/notAuthorized/$",
        params: { _splat: location.href }
      });
    }
    const communityId = Number(params.communityId);
    const { secureAxios } = context.apiHook;
    const community = context.queryClient.getQueryData<{
      name: string;
      id: number;
    }>(getCommunityDetailsByIdQuery(secureAxios, communityId).queryKey);
    return {
      crumb: community?.name || "Community"
    };
  }
});

function RouteComponent() {
  const { communityId } = Route.useParams();
  const { secureAxios } = useApi();
  const { data: community } = useQuery(
    getCommunityDetailsByIdQuery(secureAxios, Number(communityId))
  );

  if (!community) {
    return <LoadingPane />;
  }

  return (
    <Box>
      <Title order={3}>{community.name}</Title>
      <RoutedTabs
        route={Route}
        tabs={[
          { value: "roles", label: "Roles" },
          { value: "users", label: "Users" },
          { value: "permissions", label: "Permissions" }
        ]}
      />
      <Box my={20}>
        <Outlet />
      </Box>
    </Box>
  );
}
